'use client';

import { useCheckout } from '../hooks/useCheckout';
import { usePlans } from '../hooks/usePlans';
import type { Interval, Tier } from '../types/billing.types';

interface StripeCheckoutButtonProps {
  tier: Exclude<Tier, 'free' | 'starter'>;
  interval: Interval;
  children?: React.ReactNode;
}

/**
 * Standalone "subscribe with card" button — handy for the dashboard upsell
 * or anywhere the full plan-comparison grid would be overkill.
 *
 * Reflects real config: when Stripe isn't enabled the button is disabled
 * with a "Payments coming soon" tooltip. No hardcoded fallback text.
 */
export function StripeCheckoutButton({
  tier,
  interval,
  children = 'Subscribe with card',
}: StripeCheckoutButtonProps) {
  const { data: plans } = usePlans();
  const { startCheckout, isLoading } = useCheckout();
  const enabled = Boolean(plans?.stripe_enabled);

  if (!enabled) {
    return (
      <button
        type="button"
        disabled
        title="Payments coming soon"
        className="rounded-md bg-coral px-4 py-2 text-sm font-medium text-paper opacity-60"
      >
        {children}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => startCheckout({ tier, interval, provider: 'stripe' })}
      disabled={isLoading}
      className="rounded-md bg-coral px-4 py-2 text-sm font-medium text-paper disabled:opacity-60"
    >
      {isLoading ? 'Starting…' : children}
    </button>
  );
}
